import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import defaultIcon from "../../../assets/teamicon.png";
import ExportStandingsModal from "../../../components/ExportModal";
import { jwtDecode } from "jwt-decode";

const StandingsTable = ({ tournament, selectedSubcategory }) => {
  const token = useSelector((state) => state.user.token);
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userId, setUserId] = useState("");

  // Controladores de estado para el menú de acciones
  const [activeMenu, setActiveMenu] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0); // Para re-renderizar tras actualizar

  useEffect(() => {
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        setUserId(decodedToken.id);
      } catch (error) {}
    }
  }, [token]);

  useEffect(() => {
    const fetchStandings = async () => {
      try {
        setLoading(true);
        let url = `${process.env.REACT_APP_API_URL}/api/standing?tournament_id=${tournament.id}`;
        if (selectedSubcategory) {
          url += `&subcategory_id=${selectedSubcategory.id}`;
        }

        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) throw new Error("Error al obtener las posiciones");

        const data = await response.json();
        setStandings(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchStandings();
  }, [tournament.id, selectedSubcategory, token, refreshTrigger]);

  // Manejador para eliminar/readmitir equipos
  const handleToggleElimination = async (team) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/standing/toggle-elimination`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            tournament_id: tournament.id,
            team_id: team.team_id,
            subcategory_id: selectedSubcategory ? selectedSubcategory.id : null,
            is_eliminated: !team.is_eliminated,
          }),
        },
      );

      if (!response.ok)
        throw new Error("No se pudo actualizar el estado del equipo");

      setActiveMenu(null);
      setRefreshTrigger((prev) => prev + 1);
    } catch (err) {
      alert(`⚠️ Error: ${err.message}`);
    }
  };

  if (error) {
    return (
      <div className="flex items-center justify-center p-6 border border-red-100 bg-red-50 rounded-2xl">
        <p className="text-red-600 text-sm font-medium">⚠️ {error}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-4 animate-pulse">
        <div className="h-8 bg-gray-200 rounded-lg mb-4 w-1/4"></div>
        {[1, 2, 3, 4, 5].map((n) => (
          <div
            key={n}
            className="flex items-center gap-4 py-3 border-b border-gray-50 last:border-0"
          >
            <div className="h-4 bg-gray-200 rounded w-6"></div>
            <div className="h-8 bg-gray-200 rounded-full w-8"></div>
            <div className="h-4 bg-gray-200 rounded w-1/3"></div>
            <div className="h-4 bg-gray-200 rounded w-full flex-1 hidden md:block"></div>
          </div>
        ))}
      </div>
    );
  }

  if (!standings.length) {
    return (
      <div className="text-center py-12 px-4 bg-white rounded-2xl border border-dashed border-gray-200">
        <p className="text-gray-400 font-medium">
          No hay datos de posiciones aún.
        </p>
        <p className="text-gray-400 text-xs mt-1">
          Las estadísticas se actualizarán cuando comiencen los partidos.
        </p>
      </div>
    );
  }

  const getPositionStyle = (index, isEliminated) => {
    if (isEliminated)
      return "text-red-400/80 font-bold bg-red-50 rounded-lg px-2 py-0.5 text-xs";
    if (index === 0)
      return "text-amber-500 font-bold bg-amber-50/50 rounded-lg px-2 py-0.5";
    if (index === 1)
      return "text-slate-400 font-bold bg-slate-50 rounded-lg px-2 py-0.5";
    if (index === 2)
      return "text-amber-700 font-bold bg-amber-50 rounded-lg px-2 py-0.5";
    return "text-gray-500 font-medium px-2";
  };

  return (
    <>
      <div className="relative overflow-visible rounded-2xl border border-gray-100 bg-white shadow-sm">
        {userId === tournament.user_id && (
          <button
            onClick={() => setIsModalOpen(true)}
            className="absolute top-3 right-3 z-20 h-9 w-9 flex items-center justify-center rounded-xl bg-white/90 backdrop-blur border border-gray-200 text-gray-600 shadow-sm hover:bg-indigo-600 hover:text-white hover:border-indigo-600 transition-all"
            title="Exportar tabla"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
              />
            </svg>
          </button>
        )}

        <div className="overflow-x-auto overflow-y-visible">
          <table className="min-w-full border-collapse text-sm">
            <thead className="bg-gray-50/70 border-b border-gray-100 uppercase tracking-wider text-xs">
              <tr>
                <th className="p-4 text-center font-semibold text-gray-500 sticky left-0 bg-gray-50 z-10 w-12">
                  #
                </th>
                <th className="p-4 text-left font-semibold text-gray-500 sticky left-12 bg-gray-50 z-10 min-w-[240px]">
                  Equipo
                </th>
                <th className="p-4 text-center font-semibold text-gray-400">
                  PJ
                </th>
                <th className="p-4 text-center font-semibold text-emerald-600/80">
                  G
                </th>
                <th className="p-4 text-center font-semibold text-gray-400">
                  E
                </th>
                <th className="p-4 text-center font-semibold text-rose-600/80">
                  P
                </th>
                <th className="p-4 text-center font-semibold text-gray-400 hidden sm:table-cell">
                  GF
                </th>
                <th className="p-4 text-center font-semibold text-gray-400 hidden sm:table-cell">
                  GC
                </th>
                <th className="p-4 text-center font-semibold text-gray-500">
                  DG
                </th>
                <th className="p-4 text-center font-bold text-gray-700 bg-gray-50/50">
                  Pts
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {standings.map((team, index) => {
                const isEliminated = team.is_eliminated;
                const menuKey = `${team.team_id}-${team.team_category_id}`;

                return (
                  <tr
                    key={menuKey}
                    className={`transition-colors group ${
                      isEliminated
                        ? "bg-red-50/30 hover:bg-red-50/60"
                        : "hover:bg-slate-50/80"
                    }`}
                  >
                    {/* Posición */}
                    <td
                      className={`p-4 text-center sticky left-0 z-10 transition-colors ${
                        isEliminated
                          ? "bg-red-50/50 group-hover:bg-red-50/80"
                          : "bg-white group-hover:bg-slate-50/80"
                      }`}
                    >
                      <span className={getPositionStyle(index, isEliminated)}>
                        {isEliminated ? "OUT" : index + 1}
                      </span>
                    </td>

                    {/* Equipo + Menú Flotante */}
                    <td
                      className={`p-4 sticky left-12 z-10 transition-colors relative ${
                        isEliminated
                          ? "bg-red-50/50 group-hover:bg-red-50/80"
                          : "bg-white group-hover:bg-slate-50/80"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2 pr-8">
                        <div className="flex items-center gap-3 min-w-0">
                          <img
                            src={team.picture_url || defaultIcon}
                            alt={team.team_name}
                            className={`w-8 h-8 rounded-full object-cover shadow-sm ring-2 ${
                              isEliminated
                                ? "ring-red-100 opacity-60 grayscale"
                                : "bg-gray-50 ring-gray-100"
                            }`}
                            onError={(e) => (e.target.src = defaultIcon)}
                          />
                          <div className="flex flex-col min-w-0">
                            <div className="flex items-center gap-2">
                              <span
                                className={`font-semibold truncate ${
                                  isEliminated
                                    ? "text-red-800/60"
                                    : "text-gray-800"
                                }`}
                              >
                                {team.team_name}
                              </span>
                            </div>
                            {team.group_name && (
                              <span
                                className={`text-xs font-medium ${isEliminated ? "text-red-400/60" : "text-gray-400"}`}
                              >
                                {team.group_name}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Botón de 3 puntos (Sólo visible para el creador en Hover) */}
                        {userId === tournament.user_id && (
                          <div className="absolute right-2 top-1/2 -translate-y-1/2 z-20">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveMenu(
                                  activeMenu === menuKey ? null : menuKey,
                                );
                              }}
                              className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-all bg-white shadow-sm border border-gray-100"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path d="M12 10a2 2 0 100 4 2 2 0 000-4zM12 4a2 2 0 100 4 2 2 0 000-4zM12 16a2 2 0 100 4 2 2 0 000-4z" />
                              </svg>
                            </button>

                            {/* Desplegable de acciones */}
                            {activeMenu === menuKey && (
                              <>
                                <div
                                  className="fixed inset-0 z-30"
                                  onClick={() => setActiveMenu(null)}
                                />
                                <div className="absolute top-1/2 right-full transform -translate-y-1/2 mr-2 w-44 bg-white border border-gray-100 rounded-xl shadow-xl z-40 py-1 overflow-hidden whitespace-nowrap animate-in fade-in slide-in-from-right-2 duration-150">
                                  <button
                                    onClick={() =>
                                      handleToggleElimination(team)
                                    }
                                    className={`w-full text-left px-4 py-2.5 text-xs font-semibold flex items-center gap-2 transition-colors ${
                                      isEliminated
                                        ? "text-emerald-600 hover:bg-emerald-50"
                                        : "text-rose-600 hover:bg-rose-50"
                                    }`}
                                  >
                                    {isEliminated ? (
                                      <>
                                        <svg
                                          className="w-3.5 h-3.5"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2.5"
                                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                          />
                                        </svg>
                                        Readmitir equipo
                                      </>
                                    ) : (
                                      <>
                                        <svg
                                          className="w-3.5 h-3.5"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2.5"
                                            d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                                          />
                                        </svg>
                                        Marcar Eliminado
                                      </>
                                    )}
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Estadísticas */}
                    <td
                      className={`p-4 text-center ${isEliminated ? "text-red-400/60" : "text-gray-600"}`}
                    >
                      {team.played}
                    </td>
                    <td
                      className={`p-4 text-center font-medium ${isEliminated ? "text-red-400/60" : "text-gray-600"}`}
                    >
                      {team.won}
                    </td>
                    <td
                      className={`p-4 text-center ${isEliminated ? "text-red-400/60" : "text-gray-500"}`}
                    >
                      {team.drawn}
                    </td>
                    <td
                      className={`p-4 text-center ${isEliminated ? "text-red-400/60" : "text-gray-600"}`}
                    >
                      {team.lost}
                    </td>
                    <td
                      className={`p-4 text-center hidden sm:table-cell ${isEliminated ? "text-red-400/60" : "text-gray-500"}`}
                    >
                      {team.goals_for}
                    </td>
                    <td
                      className={`p-4 text-center hidden sm:table-cell ${isEliminated ? "text-red-400/60" : "text-gray-500"}`}
                    >
                      {team.goals_against}
                    </td>
                    <td
                      className={`p-4 text-center font-medium ${
                        isEliminated
                          ? "text-red-400/60"
                          : team.goal_difference > 0
                            ? "text-emerald-600"
                            : team.goal_difference < 0
                              ? "text-rose-600"
                              : "text-gray-400"
                      }`}
                    >
                      {team.goal_difference > 0
                        ? `+${team.goal_difference}`
                        : team.goal_difference}
                    </td>

                    {/* Puntos */}
                    <td
                      className={`p-4 text-center font-bold text-base transition-colors ${
                        isEliminated
                          ? "text-red-800/60 bg-red-100/30 group-hover:bg-red-100/50"
                          : "text-gray-900 bg-gray-50/30 group-hover:bg-slate-100/50"
                      }`}
                    >
                      {team.points}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <ExportStandingsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        standings={standings}
        subcategory={selectedSubcategory}
      />
    </>
  );
};

export default StandingsTable;
